<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, ButtonGroup, Spinner, TabContent, TabPane } from '@sveltestrap/sveltestrap';
  import { differenceInCalendarDays, endOfDay } from 'date-fns';

  import { ActivityHistory } from '@client/components/activity-history';
  import { AudienceInsights } from '@client/components/audience-insights';
  import { DatePicker } from '@client/components/datepicker';
  import { Icon } from '@client/components/icon';
  import { WebsiteTracking } from '@client/components/website-tracking';

  import { AdsService } from '@client/services/ads';

  import { activeDomain, internalDomains } from '@client/stores/domain-settings';

  import { isInternalRecipient, MessageLogParser } from '@client/utils/activity';

  import { formatDate } from '@common/utils/dates';
  import { getDateFromToday } from '@common/utils/dates';

  import { People } from './tabs';

  export let row = {};
  export let days = 365;
  export let showRange = false;

  let history = [];
  let loading = false;
  let recipientFilter = null;

  // Custom date range — mutually exclusive with the preset lookback buttons
  let customStart = null;
  let customEnd = null;
  let customPickerOpen = false;
  $: isCustomRange = !!(customStart && customEnd);

  let engagement = {
    activity: [],
    recips: {}
  };

  let dateFormat = 'MMMM d, yyyy';
  let datesFormatted =
    formatDate(getDateFromToday(days - 1), dateFormat) + ' - ' + formatDate(getDateFromToday(0), dateFormat);

  const lookbackOptions = [
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 30, time: $format('label.DAYS') } }),
      value: 30
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 60, time: $format('label.DAYS') } }),
      value: 60
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 90, time: $format('label.DAYS') } }),
      value: 90
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 6, time: $format('label.MONTHS') } }),
      value: 180
    },
    {
      label: $format('label.TIME_PERIOD', { values: { amount: 1, time: $format('label.YEAR') } }),
      value: 365
    },
    {
      label: $format('label.DATES_ALL_TIME'),
      value: 365 * 10
    }
  ];

  const onLookbackChange = async (value) => {
    customStart = null;
    customEnd = null;
    customPickerOpen = false;
    days = value;
    datesFormatted =
      formatDate(getDateFromToday(days - 1), dateFormat) + ' - ' + formatDate(getDateFromToday(0), dateFormat);
  };

  const onCustomDateChange = ({ startDate, endDate }) => {
    if (!startDate || !endDate) return;
    customStart = startDate;
    customEnd = endDate;
    customPickerOpen = false;
    // Fetch enough days to reach the custom start; getClickReport filters down to the chosen window
    days = differenceInCalendarDays(new Date(), new Date(customStart)) + 1;
    datesFormatted =
      formatDate(new Date(customStart), dateFormat) + ' - ' + formatDate(new Date(customEnd), dateFormat);
  };

  const onTab = (evt) => {
    switch (evt.detail) {
      case 'engagement':
        initialized.engagement = true;
        break;
      case 'audience':
        initialized.audience = true;
        break;
    }
  };

  const getClickReport = async () => {
    loading = true;

    const id = row._id;
    const response = await AdsService.getClickReport(id, days);

    loading = false;

    // The clickdata endpoint returns every click; when "Allow Internal" is off, drop internal
    // recipients here so the activity section matches the performance metrics (which already
    // exclude internal server-side via the ad's countinternal flag).
    let clicks =
      row.countinternal || !Array.isArray(response)
        ? response
        : response.filter((item) => !isInternalRecipient(item.recipient, $activeDomain, $internalDomains));

    // Narrow to the chosen custom window (the endpoint only takes a lookback count, not an end date)
    if (isCustomRange && Array.isArray(clicks)) {
      const start = new Date(customStart);
      const end = endOfDay(new Date(customEnd));
      clicks = clicks.filter((item) => {
        const timeslice = new Date(item.utcts);
        return timeslice >= start && timeslice <= end;
      });
    }

    history = clicks;

    const formatted = MessageLogParser.buildMsgLogReports(clicks);

    formatted.forEach((item) => {
      Object.entries(item.recips).forEach(([msghashid, email]) => {
        engagement.recips[msghashid] = email;
      });

      return item;
    });

    engagement.recips = Object.keys(engagement.recips)
      .sort((a, b) => engagement.recips[a].localeCompare(engagement.recips[b]))
      .reduce((acc, key) => ((acc[key] = engagement.recips[key]), acc), {});
  };

  onMount(getClickReport);

  let initialized = {
    audience: false,
    engagement: false
  };

  $: getClickReport(days, customStart, customEnd);
</script>

{#if !loading}
  {#if showRange}
    <div class="report">
      <div class="range">
        <div class="date-range">
          <Icon name="calendar" />
          {datesFormatted}
        </div>
        <DatePicker
          isRange
          isMultipane
          align="right"
          bind:isOpen={customPickerOpen}
          startDate={customStart}
          endDate={customEnd}
          onDateChange={onCustomDateChange}
        >
          <ButtonGroup>
            <Button size="sm" active={isCustomRange} on:click={() => (customPickerOpen = !customPickerOpen)}>
              {$format('label.CUSTOM')}
            </Button>
            {#each lookbackOptions as option}
              <Button
                size="sm"
                active={!isCustomRange && days === option.value}
                on:click={() => onLookbackChange(option.value)}
              >
                {option.label}
              </Button>
            {/each}
          </ButtonGroup>
        </DatePicker>
      </div>
    </div>
  {/if}
  <div class="activity-details">
    <div class="report-tour"></div>
    <div class="activity-details-tabs">
      <TabContent on:tab={onTab} pills>
        <TabPane tabId="history" tab={$format('label.HISTORY')} active>
          {#if loading}
            <div class="empty">{$format('label.LOADING')}...</div>
          {:else}
            <ActivityHistory data={history} previews={false} includeSender bind:recipientFilter allowReporting />
          {/if}
        </TabPane>
        <TabPane tabId="engagement" tab={$format('label.ENGAGEMENT')}>
          {#if initialized.engagement}
            {#if Object.keys(engagement).length}
              <WebsiteTracking row={engagement} bind:recipientFilter />
            {/if}
          {/if}
        </TabPane>
        <TabPane tabId="audience" tab={$format('label.AUDIENCE')}>
          {#if initialized.audience}
            <AudienceInsights row={{ activity: history }} bind:recipientFilter />
          {/if}
        </TabPane>
      </TabContent>
    </div>
    <div>
      <People row={engagement} {history} bind:recipientFilter />
    </div>
  </div>
{:else}
  <div class="loading">
    <Spinner show inline style="border-width: 2px; color: #0087ff; height: 16px; width: 16px;" />
    {$format('label.LOADING')}...
  </div>
{/if}

<style lang="scss" src="./row-expansion.scss"></style>
